/**
 * Serviço de Cálculo de Balanço Hídrico para Irrigação
 * 
 * Calcula a necessidade de irrigação baseado em:
 * - ET0 (Evapotranspiração de Referência)
 * - Kc (Coeficiente de Cultura por fase fenológica)
 * - Precipitação
 * - CAD (Capacidade de Água Disponível do solo)
 * - Armazenamento atual de água no solo
 * 
 * Referência: FAO-56 Manual de Irrigação
 */

import { ET0Calculator } from './ET0Calculator';
import { supabase } from '../supabaseClient';
import { DadosMeteorologicosService } from './DadosMeteorologicosService';
import { isFutureLocalDate } from '../utils/localDate';

export class BalancoHidricoService {
  
  /**
   * Calcula o balanço hídrico para um talhão específico
   * 
   * @param {Object} config - Configuração do talhão
   * @param {Object} dadosMeteorologicos - Dados meteorológicos do dia
   * @param {number} armazenamentoAnterior - Armazenamento do dia anterior (mm)
   * @param {number} irrigacaoAplicada - Lâmina de irrigação aplicada (mm)
   * @param {number} diasAposPlantio - Dias após o plantio (para calcular Kc)
   * @returns {Object} Resultado do balanço hídrico
   */
  static calcularBalancoDiario(
    config,
    dadosMeteorologicos,
    armazenamentoAnterior = null,
    irrigacaoAplicada = 0,
    diasAposPlantio = 0,
    cadAnterior = null
  ) {
    try {
      // 1. Calcular ET0 (evapotranspiração de referência)
      const et0 = this.calcularET0(dadosMeteorologicos, config);
      
      if (!et0) {
        console.warn('Não foi possível calcular ET0');
        return null;
      }
      
      // 2. Determinar Kc pela curva FAO-56 de coeficiente único.
      const resultadoKc = this.calcularKc(config, diasAposPlantio, dadosMeteorologicos);
      const { kc, faseFenologica } = resultadoKc;
      const zrFase = this.calcularZr(config, diasAposPlantio);
      
      // 3. Calcular ETc ajustada ao sistema. Em irrigação localizada,
      // P é o maior valor entre a área molhada e a área sombreada.
      const etcSemLocalizacao = et0 * kc;
      const { kl, percentualLocalizacao } = this.calcularCoeficienteLocalizacao(config);
      const etc = etcSemLocalizacao * kl;
      
      // 4. Obter precipitação do dia
      const precipitacao = dadosMeteorologicos.precipitacao_mm || 0;
      
      // 5. Calcular CAD dinâmica para a fase atual
      // Se configurado por textura (cadCmSolo), recalcula com Zr da fase
      // Senão usa a CAD fixa salva na configuração
      const cadBase = config.capacidade_agua_disponivel || 75;
      const cadCmSolo = config.cad_por_cm_solo; // mm/cm — setado se método simples/avançado
      const CAD = cadCmSolo ? parseFloat((cadCmSolo * zrFase).toFixed(2)) : cadBase;
      
      // Se não há armazenamento anterior, assumir 100% da CAD (solo bem irrigado)
      let armAnterior = armazenamentoAnterior !== null ? armazenamentoAnterior : CAD;
      
      // Quando o Zr avança de fase e a CAD cresce, a camada de solo recém-incorporada
      // já existia e assume-se próxima da capacidade de campo (FAO-56) — sem isso, o
      // percentual cairia artificialmente sem perda real de água.
      if (cadAnterior != null && CAD > cadAnterior) {
        armAnterior = Math.min(CAD, armAnterior + (CAD - cadAnterior));
      }
      
      // Balanço: Armazenamento = Anterior + Entradas - Saídas
      let armazenamentoAtual = armAnterior + precipitacao + irrigacaoAplicada - etc;
      
      // Limitar entre 0 e CAD
      let excesso = 0;
      if (armazenamentoAtual > CAD) {
        excesso = armazenamentoAtual - CAD;
        armazenamentoAtual = CAD;
      } else if (armazenamentoAtual < 0) {
        armazenamentoAtual = 0;
      }
      
      // 6. Calcular percentual de água disponível
      const percentualAgua = (armazenamentoAtual / CAD) * 100;
      
      // 7. Calcular déficit acumulado
      const deficitAcumulado = Math.max(0, CAD - armazenamentoAtual);
      
      // 8. Calcular necessidade de irrigação baseada no fator de disponibilidade
      const resultadoFad = this.calcularFatorDisponibilidadeAjustado(config, etc);
      const fatorf = resultadoFad.fator;
      // RAW = p × CAD; em armazenamento, o limiar é CAD × (1 - p).
      // Ex.: p = 0,6 permite deplecionar 60% da CAD e aciona a 40% da CAD.
      const armazenamentoCritico = CAD * (1 - fatorf);
      const necessidadeIrrigacao = this.calcularNecessidadeIrrigacao(
        armazenamentoAtual,
        armazenamentoCritico,
        CAD,
        config
      );
      
      // 9. Determinar recomendação e prioridade
      const { recomendacao, prioridade } = this.gerarRecomendacao(
        percentualAgua,
        armazenamentoAtual,
        armazenamentoCritico,
        necessidadeIrrigacao,
        precipitacao
      );
      
      // 10. Estimar dias até o armazenamento atingir o limite crítico.
      const diasAteCritico = etc > 0
        ? Math.max(0, Math.floor((armazenamentoAtual - armazenamentoCritico) / etc))
        : 999;
      
      return {
        // Demanda
        et0_dia: parseFloat(et0.toFixed(2)),
        kc_dia: parseFloat(kc.toFixed(2)),
        kc_base_dia: parseFloat(resultadoKc.kcBase.toFixed(3)),
        correcao_climatica_kc: parseFloat(resultadoKc.correcaoClimatica.toFixed(3)),
        vento_kc_dia: parseFloat(resultadoKc.vento.toFixed(2)),
        rh_min_kc_dia: parseFloat(resultadoKc.rhMin.toFixed(2)),
        origem_ajuste_kc: resultadoKc.origemAjuste,
        kl_dia: parseFloat(kl.toFixed(3)),
        percentual_localizacao: parseFloat(percentualLocalizacao.toFixed(2)),
        etc_sem_localizacao_dia: parseFloat(etcSemLocalizacao.toFixed(2)),
        etc_dia: parseFloat(etc.toFixed(2)),
        fase_fenologica: faseFenologica,
        metodo_calculo_et0: dadosMeteorologicos.metodo_et0 || 'Penman-Monteith',
        
        // Entradas
        precipitacao_mm: parseFloat(precipitacao.toFixed(2)),
        irrigacao_mm: parseFloat(irrigacaoAplicada.toFixed(2)),
        
        // Balanço
        armazenamento_atual: parseFloat(armazenamentoAtual.toFixed(2)),
        armazenamento_percentual: parseFloat(percentualAgua.toFixed(2)),
        deficit_acumulado: parseFloat(deficitAcumulado.toFixed(2)),
        excesso_agua: parseFloat(excesso.toFixed(2)),
        necessidade_irrigacao: parseFloat(necessidadeIrrigacao.toFixed(2)),
        
        // Threshold FAD em mm (necessário para recalcular dias com ETc média externa)
        // Nome legado mantido para o processamento da série histórica.
        fad_mm: parseFloat(armazenamentoCritico.toFixed(2)),
        // CAD do dia (mm) — não persistida, usada apenas para encadear o rescale entre fases.
        cad_mm: parseFloat(CAD.toFixed(2)),
        fator_disponibilidade_dia: parseFloat(fatorf.toFixed(3)),
        origem_ajuste_fad: resultadoFad.origem,
        
        // Previsão e status
        dias_ate_limite_critico: diasAteCritico,
        recomendacao,
        prioridade
      };
      
    } catch (error) {
      console.error('Erro ao calcular balanço hídrico:', error);
      return null;
    }
  }
  
  /**
   * Calcula ET0 usando dados meteorológicos disponíveis
   */
  static calcularET0(dados, config) {
    const {
      temperatura_max,
      temperatura_min,
      umidade_relativa,
      vento_velocidade,
      radiacao_solar,
      data
    } = dados;
    
    // Usar coordenadas reais embutidas nos dados (preenchidas por processarPropriedade)
    const latitude = dados.lat_usada ?? -20;
    const altitude = dados.altitude_usada ?? 800;
    
    // Tentar Penman-Monteith primeiro (método mais preciso)
    if (temperatura_max != null && temperatura_min != null && umidade_relativa != null && umidade_relativa > 0) {
      const et0PM = ET0Calculator.calcularPenmanMonteith(
        temperatura_max,
        temperatura_min,
        umidade_relativa,
        vento_velocidade || 2.0,
        radiacao_solar,
        latitude,
        altitude,
        data ? new Date(data) : new Date()
      );
      
      if (et0PM !== null) {
        return et0PM;
      }
    }
    
    // Fallback: Hargreaves-Samani (apenas temperaturas)
    if (temperatura_max != null && temperatura_min != null) {
      const et0HS = ET0Calculator.calcularHargreavesSamani(
        temperatura_max,
        temperatura_min,
        latitude,
        data ? new Date(data) : new Date()
      );
      
      return et0HS;
    }
    
    return null;
  }
  
  /**
   * Calcula o Kc baseado nos dias após plantio e coeficientes configurados
   */
  static calcularKc(config, diasAposPlantio, dadosMeteorologicos = {}) {
    const {
      kc_inicial = 0.4,
      kc_meio = 1.15,
      kc_final = 0.5,
      duracao_fase_inicial = 20,
      duracao_fase_desenvolvimento = 35,
      duracao_fase_meio = 40,
      duracao_fase_final = 25,
    } = config;

    const lIni = Math.max(1, Number(duracao_fase_inicial) || 20);
    const lDev = Math.max(1, Number(duracao_fase_desenvolvimento) || 35);
    const lMid = Math.max(1, Number(duracao_fase_meio) || 40);
    const lLate = Math.max(1, Number(duracao_fase_final) || 25);
    const dia = Math.max(0, Number(diasAposPlantio) || 0);

    const clima = this.calcularAjusteClimaticoKc(config, dadosMeteorologicos);
    const kcMidBase = Number(kc_meio);
    const kcEndBase = Number(kc_final);
    const kcMidAjustado = kcMidBase + clima.correcao;
    const kcEndAjustado = kcEndBase >= 0.45 ? kcEndBase + clima.correcao : kcEndBase;

    let kcBase;
    let kc;
    let faseFenologica;
    if (dia < lIni) {
      kcBase = Number(kc_inicial);
      kc = kcBase;
      faseFenologica = 'inicial';
    } else if (dia < lIni + lDev) {
      const progresso = (dia - lIni) / lDev;
      kcBase = Number(kc_inicial) + progresso * (kcMidBase - Number(kc_inicial));
      kc = Number(kc_inicial) + progresso * (kcMidAjustado - Number(kc_inicial));
      faseFenologica = 'desenvolvimento';
    } else if (dia < lIni + lDev + lMid) {
      kcBase = kcMidBase;
      kc = kcMidAjustado;
      faseFenologica = 'meio';
    } else {
      const progresso = Math.min(1, (dia - lIni - lDev - lMid) / lLate);
      kcBase = kcMidBase + progresso * (kcEndBase - kcMidBase);
      kc = kcMidAjustado + progresso * (kcEndAjustado - kcMidAjustado);
      faseFenologica = 'final';
    }

    return {
      kc,
      kcBase,
      faseFenologica,
      correcaoClimatica: kc - kcBase,
      vento: clima.vento,
      rhMin: clima.rhMin,
      origemAjuste: clima.origem,
    };
  }

  /** Ajuste climático FAO-56, equações 62 e 65. */
  static calcularAjusteClimaticoKc(config = {}, dados = {}) {
    if (!config.ajustar_kc_clima) {
      return { correcao: 0, vento: 2, rhMin: 45, origem: 'FAO-56 tabulado' };
    }

    const limitar = (valor, minimo, maximo) => Math.min(maximo, Math.max(minimo, valor));
    const ventoInformado = Number(dados.vento_velocidade);
    const vento = limitar(ventoInformado > 0 ? ventoInformado : 2, 1, 6);
    let rhMin = Number(dados.umidade_relativa_minima);
    let origemRh = 'RHmin medida';

    if (!Number.isFinite(rhMin)) {
      const tMax = Number(dados.temperatura_max);
      const tMin = Number(dados.temperatura_min);
      if (Number.isFinite(tMax) && Number.isFinite(tMin)) {
        const pressaoSaturacao = (temperatura) => 0.6108 * Math.exp((17.27 * temperatura) / (temperatura + 237.3));
        rhMin = 100 * pressaoSaturacao(tMin) / pressaoSaturacao(tMax);
        origemRh = 'RHmin estimada pela FAO-56 Eq. 64 (Tmin/Tmax)';
      } else {
        rhMin = 45;
        origemRh = 'RHmin padrão FAO-56';
      }
    }

    rhMin = limitar(rhMin, 20, 80);
    const altura = limitar(Number(config.altura_cultura_m) || 0.5, 0.1, 10);
    const correcao = (0.04 * (vento - 2) - 0.004 * (rhMin - 45)) * Math.pow(altura / 3, 0.3);
    const origemVento = ventoInformado > 0 ? 'vento medido/estimado a 2 m' : 'vento padrão 2 m/s';

    return {
      correcao,
      vento,
      rhMin,
      origem: `${origemVento}; ${origemRh}`,
    };
  }

  /**
   * Retorna a profundidade de raiz (Zr) em cm, interpolada continuamente durante o
   * desenvolvimento (de zr_inicial a zr_meio), assim como o Kc já é interpolado.
   * Evita um salto abrupto de CAD logo no primeiro dia de cada fase.
   */
  static calcularZr(config = {}, diasAposPlantio = 0) {
    const {
      zr_inicial = 20,
      zr_meio = 70,
      zr_final = 70,
      duracao_fase_inicial = 20,
      duracao_fase_desenvolvimento = 35,
      duracao_fase_meio = 40,
    } = config;

    const lIni = Math.max(1, Number(duracao_fase_inicial) || 20);
    const lDev = Math.max(1, Number(duracao_fase_desenvolvimento) || 35);
    const lMid = Math.max(1, Number(duracao_fase_meio) || 40);
    const dia = Math.max(0, Number(diasAposPlantio) || 0);

    if (dia < lIni) return zr_inicial;
    if (dia < lIni + lDev) {
      const progresso = (dia - lIni) / lDev;
      return zr_inicial + progresso * (zr_meio - zr_inicial);
    }
    if (dia < lIni + lDev + lMid) return zr_meio;
    return zr_final;
  }

  /**
   * Keller/Bliesner: Kl = 0,1 × √P, com P = max(PAM, PAS) em porcentagem.
   * O ajuste se aplica somente a gotejamento e microaspersão.
   */
  static calcularCoeficienteLocalizacao(config = {}) {
    const sistemasLocalizados = new Set(['gotejamento', 'microaspersao']);
    if (!sistemasLocalizados.has(config.sistema_irrigacao)) {
      return { kl: 1, percentualLocalizacao: 100 };
    }

    const normalizarPercentual = (valor) => {
      if (valor === null || valor === undefined || valor === '') return 100;
      const numero = Number(valor);
      return Number.isFinite(numero) ? Math.min(100, Math.max(1, numero)) : 100;
    };
    const pam = normalizarPercentual(config.area_molhada_perc);
    const pas = normalizarPercentual(config.area_sombreada_perc);
    const percentualLocalizacao = Math.max(pam, pas);
    const kl = Math.min(1, 0.1 * Math.sqrt(percentualLocalizacao));

    return { kl, percentualLocalizacao };
  }

  /**
   * Ajusta a fração de depleção permitida (p) pela demanda atmosférica do dia,
   * conforme FAO-56 Tabela 22: p = p_tabelado + 0,04 × (5 − ETc), limitado a [0.1, 0.8].
   * ETc alta reduz p (aciona a irrigação mais cedo); ETc baixa aumenta p.
   * Opt-in via config.ajustar_fad_etc para não alterar comportamento de configs existentes.
   */
  static calcularFatorDisponibilidadeAjustado(config = {}, etc = 0) {
    const base = config.fator_disponibilidade || 0.5;
    if (!config.ajustar_fad_etc) {
      return { fator: base, base, correcao: 0, origem: 'Fator fixo configurado' };
    }
    const correcao = 0.04 * (5 - etc);
    const fator = Math.min(0.8, Math.max(0.1, base + correcao));
    return { fator, base, correcao, origem: 'FAO-56 Tabela 22 (p ajustado pela ETc)' };
  }

  /**
   * Calcula a lâmina necessária de irrigação
   */
  static calcularNecessidadeIrrigacao(armazenamentoAtual, armazenamentoCritico, CAD, config) {
    // A irrigação é acionada quando a depleção supera p × CAD.
    if (armazenamentoAtual < armazenamentoCritico) {
      // Irrigar para repor até próximo da CAD (ex: 90% da CAD)
      const alvo = CAD * 0.9;
      const necessidade = alvo - armazenamentoAtual;
      
      // Ajustar pela eficiência do sistema
      const eficiencia = (config.eficiencia_sistema || 85) / 100;
      return necessidade / eficiencia;
    }
    
    return 0;
  }
  
  /**
   * Gera recomendação de irrigação baseada no status hídrico
   */
  static gerarRecomendacao(percentualAgua, armazenamento, armazenamentoCritico, necessidade, precipitacao) {
    let recomendacao, prioridade;
    
    // Critérios de decisão
    if (percentualAgua > 80) {
      recomendacao = 'nao_necessario';
      prioridade = 5; // Baixa
    } else if (percentualAgua > 60) {
      recomendacao = 'nao_necessario';
      prioridade = 4;
    } else if (armazenamento >= armazenamentoCritico) {
      recomendacao = 'nao_necessario';
      prioridade = 3;
    } else if (armazenamento < armazenamentoCritico && armazenamento > armazenamentoCritico * 0.5) {
      // Abaixo do armazenamento crítico, mas ainda acima da faixa urgente
      if (precipitacao > 5) {
        recomendacao = 'aguardar_chuva';
        prioridade = 3;
      } else {
        recomendacao = 'irrigar';
        prioridade = 2; // Média-alta
      }
    } else if (armazenamento <= armazenamentoCritico * 0.5) {
      // Urgente: abaixo de 50% do armazenamento crítico
      recomendacao = 'irrigar_urgente';
      prioridade = 1; // Urgente
    } else {
      recomendacao = 'nao_necessario';
      prioridade = 3;
    }
    
    return { recomendacao, prioridade };
  }
  
  /**
   * Processa o balanço hídrico para todos os talhões ativos de uma propriedade
   * e salva os resultados no banco de dados
   */
  /**
   * Retorna um array de datas (YYYY-MM-DD) desde `dataInicio` até hoje
   * para as quais `talhaoId` ainda não tem registro em balanco_hidrico_diario.
   * @param {boolean} forcarTudo - ignora o que já existe e reprocessa o intervalo inteiro
   *   (necessário após mudança na fórmula de cálculo; não gera chamadas extras à NASA
   *   porque os dados meteorológicos já estão em cache).
   */
  static async _datasFaltantes(talhaoId, dataInicio, forcarTudo = false) {
    const hoje = new Date();
    const hojeStr = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}-${String(hoje.getDate()).padStart(2,'0')}`;
    const inicioStr = dataInicio <= hojeStr ? dataInicio : hojeStr;

    let setEstimados = new Set();
    let set = new Set();
    if (!forcarTudo) {
      const { data: existentes } = await supabase
        .from('balanco_hidrico_diario')
        .select('data, metodo_calculo_et0')
        .eq('talhao_id', talhaoId)
        .gte('data', inicioStr)
        .lte('data', hojeStr);

      // Dias estimados com média histórica devem ser reprocessados quando dado real chegar
      setEstimados = new Set(
        (existentes || [])
          .filter(r => r.metodo_calculo_et0?.includes('média'))
          .map(r => r.data)
      );
      set = new Set((existentes || []).map(r => r.data));
    }

    const faltantes = [];
    const cursor = new Date(inicioStr + 'T12:00:00');
    const fim    = new Date(hojeStr  + 'T12:00:00');
    while (cursor <= fim) {
      const s = `${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,'0')}-${String(cursor.getDate()).padStart(2,'0')}`;
      if (forcarTudo || !set.has(s) || setEstimados.has(s)) faltantes.push(s);
      cursor.setDate(cursor.getDate() + 1);
    }
    return faltantes;
  }

  /**
   * @param {string}  propriedadeId
   * @param {Date}    data          - data de referência (hoje normalmente)
   * @param {Object}  opcoes
   * @param {'nasapower'|'pular'} opcoes.fallbackSemEstacao
   *   'nasapower' → usa NASA POWER quando estação não tem dados
   *   'pular'     → deixa o dia em branco (não salva)
   * @param {boolean} opcoes.recalcularTudo
   *   Força o reprocessamento de todo o histórico (não só dias faltantes/estimados).
   *   Necessário após uma mudança na fórmula de cálculo do balanço.
   */
  static async processarPropriedade(propriedadeId, data = new Date(), opcoes = {}) {
    const { fallbackSemEstacao = 'nasapower', recalcularTudo = false } = opcoes;
    try {
      // Usar data local (não UTC) para evitar virar o dia antes do horário correto
      const dataStr = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
      
      // 0. Buscar coordenadas da propriedade (fallback quando não há estação)
      const { data: propData } = await supabase
        .from('propriedades')
        .select('latitude, longitude')
        .eq('id', propriedadeId)
        .maybeSingle();
      const propLat = propData?.latitude ?? null;
      const propLon = propData?.longitude ?? null;

      // 1. Buscar talhões ativos com configuração de irrigação
      const { data: talhoes, error: erroTalhoes } = await supabase
        .from('talhoes')
        .select(`
          id,
          nome,
          area,
          data_plantio,
          configuracao_irrigacao:configuracao_irrigacao_talhao(*)
        `)
        .eq('propriedade_id', propriedadeId)
        .is('data_colheita', null);
      
      if (erroTalhoes) {
        console.error('❌ Erro ao buscar talhões:', erroTalhoes);
        throw erroTalhoes;
      }
      
      console.log('📊 Talhões recuperados do banco:', talhoes.length);
      
      if (!talhoes || talhoes.length === 0) {
        console.log('Nenhum talhão ativo encontrado');
        return { sucesso: 0, erro: 0, mensagem: 'Nenhum talhão ativo' };
      }
      
      let processados = 0;
      let erros = 0;
      const talhoesNaoConfigurados = [];
      const talhoesInativos = [];
      const talhoesPlantioFuturo = [];
      const talhoeseSemDados = []; // sem dados meteo (estação vazia + sem coords)
      const falhasNASA = [];
      
      for (const talhao of talhoes) {
        console.log(`\n🔍 Analisando talhão: ${talhao.nome}`);
        console.log('   - ID:', talhao.id);
        console.log('   - Configuração raw:', talhao.configuracao_irrigacao);

        if (isFutureLocalDate(talhao.data_plantio, data)) {
          console.log(`⏳ Talhão ${talhao.nome}: plantio futuro em ${talhao.data_plantio}`);
          talhoesPlantioFuturo.push(talhao.nome);
          continue;
        }
        
        // Verificar se tem configuração - suportar tanto array quanto objeto direto
        let config = null;
        if (Array.isArray(talhao.configuracao_irrigacao)) {
          config = talhao.configuracao_irrigacao[0];
        } else if (talhao.configuracao_irrigacao && typeof talhao.configuracao_irrigacao === 'object') {
          config = talhao.configuracao_irrigacao;
        }
        
        console.log('   - Config extraída:', config ? 'SIM' : 'NÃO');
        if (config) {
          console.log('   - Manejo ativo:', config.manejo_ativo);
          console.log('   - Estação ID:', config.estacao_id);
          console.log('   - CAD:', config.capacidade_agua_disponivel);
        }
        
        if (!config) {
          console.log(`❌ Talhão ${talhao.nome}: sem configuração de irrigação`);
          talhoesNaoConfigurados.push(talhao.nome);
          continue;
        }
        
        if (!config.manejo_ativo) {
          console.log(`⏸️ Talhão ${talhao.nome}: manejo não está ativo`);
          talhoesInativos.push(talhao.nome);
          continue;
        }
        
        console.log(`✅ Processando talhão ${talhao.nome} com manejo ativo`);
        
        try {
          // 1b. Buscar coordenadas e tipo da estação vinculada ao talhão (se houver) — 1 única consulta,
          // reaproveitada por todo o backfill (antes era refeita a cada dia dentro do loop).
          let estLat = propLat, estLon = propLon, estAlt = 800;
          let estacaoInfo = null;
          if (config.estacao_id) {
            const { data: estacao } = await supabase
              .from('estacoes_irrigacao')
              .select('device_id, tipo_fonte, latitude, longitude, altitude')
              .eq('id', config.estacao_id)
              .maybeSingle();
            estacaoInfo = estacao || null;
            if (estacao?.latitude) estLat = estacao.latitude;
            if (estacao?.longitude) estLon = estacao.longitude;
            if (estacao?.altitude) estAlt = estacao.altitude;
          }
          talhao._lat = estLat;
          talhao._lon = estLon;
          talhao._altitude = estAlt;

          // Obter datas a processar: desde data_plantio (ou 90 dias atrás se não tiver) até hoje
          const hoje = new Date();
          const hojeStr = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}-${String(hoje.getDate()).padStart(2,'0')}`;
          let inicioBackfill;
          if (talhao.data_plantio) {
            // Nunca voltar mais de 365 dias para evitar processamento excessivo
            const plantio = talhao.data_plantio.substring(0, 10);
            const maxRetro = new Date(hoje);
            maxRetro.setDate(hoje.getDate() - 365);
            const maxRetroStr = `${maxRetro.getFullYear()}-${String(maxRetro.getMonth()+1).padStart(2,'0')}-${String(maxRetro.getDate()).padStart(2,'0')}`;
            inicioBackfill = plantio >= maxRetroStr ? plantio : maxRetroStr;
          } else {
            // Sem data de plantio: últimos 90 dias
            const d = new Date(hoje);
            d.setDate(hoje.getDate() - 89);
            inicioBackfill = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          }
          const datasFaltantes = await this._datasFaltantes(talhao.id, inicioBackfill, recalcularTudo);
          if (!datasFaltantes.includes(dataStr)) datasFaltantes.push(dataStr);
          datasFaltantes.sort(); // cronológico para encadear armazenamento

          let ultimoArmazenamento = null;

          // Buscar o armazenamento imediatamente anterior ao início do período
          const { data: armBase } = await supabase
            .from('balanco_hidrico_diario')
            .select('armazenamento_atual, armazenamento_percentual, data')
            .eq('talhao_id', talhao.id)
            .lt('data', datasFaltantes[0])
            .order('data', { ascending: false })
            .limit(1)
            .maybeSingle();
          ultimoArmazenamento = armBase?.armazenamento_atual ?? null;
          // CAD do dia anterior, derivada do percentual salvo (a coluna não guarda a CAD diretamente)
          let ultimoCAD = (armBase?.armazenamento_percentual > 0)
            ? armBase.armazenamento_atual / (armBase.armazenamento_percentual / 100)
            : null;

          // Rolling 7-day ETc array para suavizar o cálculo de dias até crítico
          const etcHistorico = [];

          // Pré-carregar ETc dos últimos 7 dias já armazenados antes do início do backfill
          const { data: etcPreBackfill } = await supabase
            .from('balanco_hidrico_diario')
            .select('etc_dia')
            .eq('talhao_id', talhao.id)
            .lt('data', datasFaltantes[0])
            .order('data', { ascending: false })
            .limit(7);
          if (etcPreBackfill) {
            etcPreBackfill.reverse().forEach(r => {
              if (r.etc_dia > 0) etcHistorico.push(r.etc_dia);
            });
          }

          let backfillRealizados = 0;

          // ── Pré-carregamento em lote (substitui consultas por dia dentro do loop) ──
          const primeiraData = datasFaltantes[0];
          const ultimaData = datasFaltantes[datasFaltantes.length - 1];
          const inicioMeteorologicoObj = new Date(primeiraData + 'T12:00:00');
          inicioMeteorologicoObj.setDate(inicioMeteorologicoObj.getDate() - 14);
          const inicioMeteorologico = inicioMeteorologicoObj.toISOString().split('T')[0];

          // 1) Eventos de irrigação executados no período inteiro, em uma única consulta
          const { data: eventosPeriodo } = await supabase
            .from('eventos_irrigacao')
            .select('data_executada, lamina_aplicada_mm')
            .eq('talhao_id', talhao.id)
            .eq('status', 'concluido')
            .gte('data_executada', primeiraData)
            .lte('data_executada', ultimaData);
          const irrigacaoPorDia = new Map();
          for (const ev of (eventosPeriodo || [])) {
            const atual = irrigacaoPorDia.get(ev.data_executada) || 0;
            irrigacaoPorDia.set(ev.data_executada, atual + (ev.lamina_aplicada_mm || 0));
          }

          // 2) Dados meteorológicos do período inteiro, em uma única consulta/lote
          const dadosMeteoPorDia = new Map();
          if (fallbackSemEstacao === 'nasapower' || !config.estacao_id) {
            if (estacaoInfo?.tipo_fonte === 'sensor_proprio' && estacaoInfo.device_id) {
              // Estação física: agrega dados horários já resumidos na view, para o período inteiro
              await this._buscarDadosEstacaoFisicaBatch(
                estacaoInfo.device_id, primeiraData, ultimaData, talhao._lat, talhao._altitude, dadosMeteoPorDia
              );
            }
            // Preenche dias restantes (sem estação física ou sem cobertura) via NASA POWER em lote
            if (talhao._lat != null && talhao._lon != null) {
              const resultadoNASA = await this._buscarDadosNASAPowerBatch(
                talhao._lat, talhao._lon, inicioMeteorologico, ultimaData, talhao.id, talhao._altitude, dadosMeteoPorDia
              );
              if (resultadoNASA.erro) {
                falhasNASA.push({ talhao: talhao.nome, mensagem: resultadoNASA.erro.message });
              }
            }
          } else if (estacaoInfo?.tipo_fonte === 'sensor_proprio' && estacaoInfo.device_id) {
            // opção 'pular': só tenta estação física
            await this._buscarDadosEstacaoFisicaBatch(
              estacaoInfo.device_id, primeiraData, ultimaData, talhao._lat, talhao._altitude, dadosMeteoPorDia
            );
          }

          const linhasParaSalvar = [];

          for (const diaStr of datasFaltantes) {
            const diaObj = new Date(diaStr + 'T12:00:00');
            const irrigacaoHoje = irrigacaoPorDia.get(diaStr) || 0;

            let dadosMeteo = dadosMeteoPorDia.get(diaStr) || null;

            if (!dadosMeteo) {
              // Tentar preenchimento com média dos últimos 7 dias (delay NASA POWER) — só ocorre
              // para os poucos dias finais ainda sem dado, não para o período inteiro.
              if (talhao._lat != null && talhao._lon != null) {
                const cacheIds = [config.estacao_id, talhao.id].filter(Boolean);
                const dadosMedia = await DadosMeteorologicosService.calcularMediaUltimosDias(
                  cacheIds, diaStr, 7
                );
                if (dadosMedia) {
                  dadosMedia.data = diaStr;
                  dadosMedia.lat_usada = talhao._lat;
                  dadosMedia.altitude_usada = talhao._altitude;
                  dadosMeteo = dadosMedia;
                  console.log(`⚡ ${talhao.nome} em ${diaStr}: sem dado NASA POWER, usando média ${dadosMedia._dias_base}d`);
                }
              }

              if (!dadosMeteo) {
                if (diaStr === dataStr) {
                  const semCoords = !talhao._lat || !talhao._lon;
                  talhoeseSemDados.push(`${talhao.nome} (${semCoords ? 'sem coordenadas' : 'est. sem dados para ' + diaStr})`);
                  erros++;
                }
                // Dia sem dados: não encadeia armazenamento, continua
                continue;
              }
            }

            // Dias após plantio
            const diasAposPlantio = talhao.data_plantio
              ? Math.floor((diaObj - new Date(talhao.data_plantio)) / (1000 * 60 * 60 * 24))
              : 30;

            // Calcular balanço encadeando armazenamento
            const balanco = this.calcularBalancoDiario(
              config, dadosMeteo, ultimoArmazenamento, irrigacaoHoje, diasAposPlantio, ultimoCAD
            );

            if (!balanco) {
              console.warn(`Falha ao calcular balanço para ${talhao.nome} em ${diaStr}`);
              if (diaStr === dataStr) erros++;
              continue;
            }

            // Atualizar histórico de ETc (janela de 7 dias)
            if (balanco.etc_dia > 0) {
              etcHistorico.push(balanco.etc_dia);
              if (etcHistorico.length > 7) etcHistorico.shift();
            }

            // Recalcular dias_ate_limite_critico com ETc média de 7 dias (mais estável que ETc de 1 único dia)
            if (etcHistorico.length > 0 && balanco.fad_mm != null) {
              const etcMedia = etcHistorico.reduce((s, v) => s + v, 0) / etcHistorico.length;
              balanco.dias_ate_limite_critico = etcMedia > 0
                ? Math.max(0, Math.floor((balanco.armazenamento_atual - balanco.fad_mm) / etcMedia))
                : 999;
            }

            // Acumula para salvar em lote (campos auxiliares não persistidos na tabela)
            const { fad_mm: _fadMm, cad_mm: _cadMm, fator_disponibilidade_dia: _fadDia, origem_ajuste_fad: _origemFad, ...balancoParaSalvar } = balanco;
            linhasParaSalvar.push({
              talhao_id: talhao.id,
              config_irrigacao_id: config.id,
              data: diaStr,
              dias_apos_plantio: diasAposPlantio,
              ...balancoParaSalvar
            });

            ultimoArmazenamento = balanco.armazenamento_atual;
            ultimoCAD = balanco.cad_mm;
            if (diaStr === dataStr) {
              processados++;
              console.log(`✅ Balanço calculado para ${talhao.nome} em ${diaStr}: ${balanco.recomendacao}`);
            } else {
              backfillRealizados++;
            }
          }

          // Salvar todos os dias do talhão em um único upsert (em vez de 1 round-trip por dia)
          if (linhasParaSalvar.length > 0) {
            const { error: erroSalvarLote } = await supabase
              .from('balanco_hidrico_diario')
              .upsert(linhasParaSalvar, { onConflict: 'talhao_id,data' });
            if (erroSalvarLote) throw erroSalvarLote;
          }

          if (backfillRealizados > 0) {
            console.log(`📅 Backfill ${talhao.nome}: ${backfillRealizados} dias preenchidos desde ${inicioBackfill}`);

            // Delay NASA POWER: hoje sem dado, mas backfill rodou com sucesso
            // → conta como sucesso em vez de erro (balanço calculado até o último dia disponível)
            const idxSemDados = talhoeseSemDados.findIndex(n => n.startsWith(talhao.nome));
            if (idxSemDados >= 0) {
              talhoeseSemDados.splice(idxSemDados, 1);
              erros = Math.max(0, erros - 1);
              processados++;
              console.log(`✅ ${talhao.nome}: balanço calculado até o último dia disponível (delay NASA POWER para hoje)`);
            }
          }
          
        } catch (erro) {
          console.error(`Erro processando talhão ${talhao.nome}:`, erro);
          erros++;
        }
      }
      
      // Construir mensagem detalhada
      let mensagens = [];
      if (processados > 0) {
        mensagens.push(`${processados} ${processados > 1 ? 'talhões' : 'talhão'} processado${processados > 1 ? 's' : ''}`);
      }
      if (talhoeseSemDados.length > 0) {
        mensagens.push(`sem dados meteo (${talhoeseSemDados.join('; ')})`);
      } else if (erros > 0) {
        mensagens.push(`${erros} erro${erros > 1 ? 's' : ''}`);
      }
      if (talhoesNaoConfigurados.length > 0) {
        mensagens.push(`${talhoesNaoConfigurados.length} sem configuração: ${talhoesNaoConfigurados.join(', ')}`);
      }
      if (talhoesInativos.length > 0) {
        mensagens.push(`${talhoesInativos.length} com manejo inativo: ${talhoesInativos.join(', ')}`);
      }
      if (talhoesPlantioFuturo.length > 0) {
        mensagens.push(`${talhoesPlantioFuturo.length} aguardando plantio: ${talhoesPlantioFuturo.join(', ')}`);
      }
      const falhasNASAUnicas = [...new Map(
        falhasNASA.map(falha => [falha.talhao, falha])
      ).values()];
      if (falhasNASAUnicas.length > 0 && processados === 0) {
        mensagens.push(`NASA POWER temporariamente indisponível para ${falhasNASAUnicas.map(f => f.talhao).join(', ')}`);
      }
      
      return {
        sucesso: processados,
        erro: erros,
        nao_configurados: talhoesNaoConfigurados.length,
        inativos: talhoesInativos.length,
        plantio_futuro: talhoesPlantioFuturo.length,
        sem_dados: talhoeseSemDados.length,
        indisponibilidade_nasa: falhasNASAUnicas.length,
        mensagem: mensagens.length > 0 ? mensagens.join(' | ') : 'Nenhum talhão processado'
      };
      
    } catch (error) {
      console.error('Erro ao processar propriedade:', error);
      throw error;
    }
  }
  
  /**
   * Busca dados meteorológicos para o cálculo do balanço hídrico.
   * Prioridade: estação física → NASA POWER → sem dados.
   *
   * @param {string|number|null} estacaoId   - ID da estação física (pode ser null)
   * @param {string}             data        - Data no formato 'YYYY-MM-DD'
   * @param {'estacao'|'nasapower'} fonte    - Fonte configurada pelo usuário
   * @param {number|null}        lat         - Latitude para NASA POWER
   * @param {number|null}        lon         - Longitude para NASA POWER
   * @param {string|number|null} talhaoId    - ID do talhão (para cache NASA POWER)
   * @param {number}             altitude    - Altitude em metros (para ET0)
   */
  static async buscarDadosMeteorologicos(
    estacaoId,
    data,
    fonte = 'estacao',
    lat = null,
    lon = null,
    talhaoId = null,
    altitude = 800
  ) {
    // 1. Estação física — usa quando a fonte configurada é 'estacao' e há ID de estação
    if (fonte === 'estacao' && estacaoId) {
      const dadosEstacao = await this._buscarDadosEstacaoFisica(
        estacaoId, data, lat, lon, altitude, talhaoId
      );
      if (dadosEstacao) return dadosEstacao;
      // Se a estação existe mas não tem dados para hoje, cai no NASA POWER (se tiver coords)
      console.warn(`⚠️ Estação ${estacaoId} sem dados para ${data}. Tentando NASA POWER...`);
    }

    // 2. NASA POWER — quando fonte é 'nasapower' OU fallback sem dados de estação
    if (lat !== null && lon !== null) {
      const dadosNASA = await this._buscarDadosNASAPowerDia(
        lat, lon, data, talhaoId, altitude
      );
      if (dadosNASA) return dadosNASA;
      console.error(`❌ NASA POWER sem dados para talhão ${talhaoId} em ${data} (lat=${lat}, lon=${lon})`);
    } else {
      // Coords ausentes — orientar o usuário
      console.error(
        `❌ Sem coordenadas para talhão ${talhaoId}. ` +
        `Preencha latitude/longitude na aba Estações ou nos dados da propriedade ` +
        `para habilitar o NASA POWER como fallback.`
      );
    }

    // 3. Sem dados disponíveis
    return null;
  }

  /**
   * Busca e agrega dados horários de uma estação física ou virtual (NASA POWER)
   * @private
   */
  static async _buscarDadosEstacaoFisica(estacaoId, data, lat, lon, altitude, talhaoId = null) {
    try {
      // Obter tipo_fonte + coordenadas da estação
      const { data: estacao, error: errEstacao } = await supabase
        .from('estacoes_irrigacao')
        .select('device_id, tipo_fonte, latitude, longitude, altitude')
        .eq('id', estacaoId)
        .single();

      if (errEstacao || !estacao) return null;

      // Estação Virtual NASA POWER: usa as coordenadas da própria estação
      if (estacao.tipo_fonte === 'nasapower') {
        const stLat = estacao.latitude ?? lat;
        const stLon = estacao.longitude ?? lon;
        const stAlt = estacao.altitude ?? altitude;
        if (stLat == null || stLon == null) {
          console.error(`❌ Estação virtual ${estacaoId} sem coordenadas para NASA POWER`);
          return null;
        }
        const dados = await this._buscarDadosNASAPowerDia(stLat, stLon, data, talhaoId ?? estacaoId, stAlt);
        if (dados) dados.fonte_dados = 'nasapower_estacao_virtual';
        return dados;
      }

      if (!estacao.device_id || estacao.tipo_fonte !== 'sensor_proprio') return null;

      const deviceId = estacao.device_id.trim().toLowerCase();

      // Calcular data seguinte para filtro de intervalo
      const [ano, mes, dia] = data.split('-').map(Number);
      const proxDia = new Date(ano, mes - 1, dia + 1);
      const proxDiaStr = `${proxDia.getFullYear()}-${String(proxDia.getMonth() + 1).padStart(2, '0')}-${String(proxDia.getDate()).padStart(2, '0')}`;

      // Buscar dados agregados diários do sensor (view dados_sensores_diarios)
      const { data: dados, error } = await supabase
        .from('dados_sensores_diarios')
        .select('*')
        .eq('device_id', deviceId)
        .gte('data_local', data)
        .lt('data_local', proxDiaStr)
        .maybeSingle();

      if (error) throw error;
      if (!dados) {
        console.warn(`⚠️ Nenhum dado na view dados_sensores_diarios para device_id=${deviceId} em ${data}`);
        return null;
      }

      console.log(`📡 Dados estação ${estacaoId} (${deviceId}) em ${data}:`, {
        temp_max: dados.temp_max,
        temp_min: dados.temp_min,
        rh_perc_media: dados.rh_perc_media,
        chuva_total: dados.chuva_total,
        leituras: dados.leituras,
        data_local: dados.data_local
      });

      if (dados.temp_max == null || dados.temp_min == null) {
        console.warn(`⚠️ Temperatura nula nos dados do sensor ${deviceId} em ${data} — sem ET0`);
        return null;
      }

      // Sensor próprio não registra vento nem radiação — serão estimados pelo ET0Calculator
      const temVento    = false;
      const temRadiacao = false;
      const metodoET0   = 'PM-FAO56 c/ vento=2m/s e radiação estimada (ΔT)';

      return {
        temperatura_max:  dados.temp_max,
        temperatura_min:  dados.temp_min,
        umidade_relativa: dados.rh_perc_media,
        vento_velocidade: 0,
        precipitacao_mm:  dados.chuva_total || 0,
        radiacao_solar:   0,
        tem_vento:        temVento,
        tem_radiacao:     temRadiacao,
        data,
        lat_usada:        lat,
        altitude_usada:   altitude,
        fonte_dados:      'estacao',
        metodo_et0:       metodoET0
      };
    } catch (error) {
      console.error('Erro ao buscar dados da estação:', error);
      return null;
    }
  }

  /**
   * Busca dados do NASA POWER para um dia específico (com cache no Supabase)
   * @private
   */
  static async _buscarDadosNASAPowerDia(lat, lon, data, talhaoId, altitude) {
    try {
      const registros = await DadosMeteorologicosService.obterDadosMeteorologicos(
        talhaoId,
        lon,   // coordenadaX = longitude
        lat,   // coordenadaY = latitude
        data,
        data
      );

      const d = registros?.[0];
      if (!d) return null;

      return {
        temperatura_max:  d.temp_max,
        temperatura_min:  d.temp_min,
        umidade_relativa: d.umidade,
        vento_velocidade: d.vento,
        precipitacao_mm:  d.precipitacao,
        radiacao_solar:   d.radiacao,
        data,
        lat_usada:        lat,
        altitude_usada:   altitude,
        fonte_dados:      'nasapower',
        metodo_et0:       'Penman-Monteith (NASA POWER)'
      };
    } catch (error) {
      console.error('Erro ao buscar dados NASA POWER:', error);
      return null;
    }
  }

  /**
   * Versão em lote de `_buscarDadosEstacaoFisica`: busca dados agregados diários do sensor
   * para um período inteiro em uma única consulta, em vez de uma consulta por dia.
   * Popula `mapaSaida` (Map<data, dadosMeteo>) diretamente.
   * @private
   */
  static async _buscarDadosEstacaoFisicaBatch(deviceId, dataInicio, dataFim, lat, altitude, mapaSaida) {
    try {
      const deviceIdNorm = deviceId.trim().toLowerCase();
      // Limite exclusivo do intervalo: dia seguinte ao fim
      const [ano, mes, dia] = dataFim.split('-').map(Number);
      const proxDia = new Date(ano, mes - 1, dia + 1);
      const proxDiaStr = `${proxDia.getFullYear()}-${String(proxDia.getMonth() + 1).padStart(2, '0')}-${String(proxDia.getDate()).padStart(2, '0')}`;

      const { data: registros, error } = await supabase
        .from('dados_sensores_diarios')
        .select('*')
        .eq('device_id', deviceIdNorm)
        .gte('data_local', dataInicio)
        .lt('data_local', proxDiaStr);

      if (error) throw error;
      if (!registros || registros.length === 0) return;

      const metodoET0 = 'PM-FAO56 c/ vento=2m/s e radiação estimada (ΔT)';
      for (const dados of registros) {
        if (dados.temp_max == null || dados.temp_min == null) continue;
        const diaStr = dados.data_local.substring(0, 10);
        mapaSaida.set(diaStr, {
          temperatura_max:  dados.temp_max,
          temperatura_min:  dados.temp_min,
          umidade_relativa: dados.rh_perc_media,
          vento_velocidade: 0,
          precipitacao_mm:  dados.chuva_total || 0,
          radiacao_solar:   0,
          tem_vento:        false,
          tem_radiacao:     false,
          data:             diaStr,
          lat_usada:        lat,
          altitude_usada:   altitude,
          fonte_dados:      'estacao',
          metodo_et0:       metodoET0
        });
      }
    } catch (error) {
      console.error('Erro ao buscar dados em lote da estação:', error);
    }
  }

  /**
   * Versão em lote de `_buscarDadosNASAPowerDia`: aproveita que `obterDadosMeteorologicos`
   * já busca o período inteiro (cache + API) de uma vez, evitando 1 round-trip por dia.
   * Popula `mapaSaida` (Map<data, dadosMeteo>) diretamente.
   * @private
   */
  static async _buscarDadosNASAPowerBatch(lat, lon, dataInicio, dataFim, talhaoId, altitude, mapaSaida) {
    try {
      const registros = await DadosMeteorologicosService.obterDadosMeteorologicos(
        talhaoId,
        lon,   // coordenadaX = longitude
        lat,   // coordenadaY = latitude
        dataInicio,
        dataFim
      );

      for (const d of (registros || [])) {
        if (mapaSaida.has(d.data)) continue; // não sobrescreve dado de estação física já preenchido
        mapaSaida.set(d.data, {
          temperatura_max:  d.temp_max,
          temperatura_min:  d.temp_min,
          umidade_relativa: d.umidade,
          vento_velocidade: d.vento,
          precipitacao_mm:  d.precipitacao,
          radiacao_solar:   d.radiacao,
          data:             d.data,
          lat_usada:        lat,
          altitude_usada:   altitude,
          fonte_dados:      'nasapower',
          metodo_et0:       'Penman-Monteith (NASA POWER)'
        });
      }
      return { quantidade: registros?.length || 0, erro: null };
    } catch (error) {
      console.error('Erro ao buscar dados NASA POWER em lote:', error);
      return { quantidade: 0, erro: error };
    }
  }
}

